import { Button, Result } from 'antd';
import { Component, type CSSProperties, type ErrorInfo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { AuthUser } from '@/redux/slices/authSlice';
import { isLazyChunkError } from '@/utils/lazyChunk';

/**
 * TCR-1189 — Error Boundary cho tầng route.
 *
 * Trước đây một lỗi render bất kỳ (đọc field undefined, map trên null, ...) sẽ
 * làm React unmount TOÀN BỘ cây và người dùng nhận về trang trắng, không nút
 * nào bấm được, không biết chuyện gì xảy ra. Boundary này chặn lỗi ở hai tầng
 * (xem `src/App.tsx` và `src/components/Layout/index.tsx`) và hiện màn hình có
 * lối thoát.
 *
 * Lỗi tải chunk được `lazyWithReload` trong `src/App.tsx` retry ngắn trước;
 * nếu vẫn thất bại (thường do vừa có bản deploy mới, chunk cũ đã bị xoá khỏi
 * server) thì đây LÀ lỗi vĩnh viễn cho session hiện tại: `React.lazy()` chỉ
 * gọi factory import() một lần, promise reject rồi thì mọi lần render sau chỉ
 * ném lại đúng lỗi cũ, không fetch lại (xem `lazyInitializer` trong
 * react-dom). Nút "Thử lại" set lại `hasError` sẽ vô tác dụng trong trường
 * hợp này — phải `window.location.reload()` để lấy index.html/manifest mới,
 * nhưng CHỈ khi user tự bấm "Thử lại" — không tự động reload khi lỗi vừa
 * xảy ra (đã bỏ theo yêu cầu, từng là hành vi TCR-1656/TCR-1940).
 * Lỗi render thường (đọc field undefined, ...) thì vẫn chỉ cần soft-reset.
 */

const FALLBACK_TITLE = 'Không tải được nội dung này';
const FALLBACK_DESCRIPTION =
  'Đã có lỗi xảy ra khi hiển thị trang. Anh/chị thử lại, hoặc quay về trang chủ.';

interface ErrorContext {
  tenant_id?: string;
  user_id?: string;
}

interface RouteErrorBoundaryInnerProps {
  children: ReactNode;
  /**
   * Đổi giá trị ⇒ tự xoá state lỗi. Tầng wrapper truyền `location.pathname`,
   * nên giá trị này đồng thời là route dùng để log.
   */
  resetKey: string;
  fullScreen?: boolean;
  context?: ErrorContext;
  onError?: (error: Error, info: ErrorInfo) => void;
  /**
   * Điều hướng về trang chủ. Tách ra prop vì hai tầng dùng hai cơ chế khác
   * nhau: tầng trong dùng router, tầng fullScreen dùng `window.location`
   * (router có thể chính là thứ đang hỏng).
   */
  onGoHome: () => void;
}

interface RouteErrorBoundaryInnerState {
  hasError: boolean;
  /** Lỗi vừa bắt được — dùng để `handleRetry` quyết định soft-reset hay reload cứng. */
  error: Error | null;
  /** Bản sao `resetKey` đã render, để so sánh trong `getDerivedStateFromProps`. */
  resetKey: string;
}

const FULL_SCREEN_STYLE: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f0f2f5',
};

const INLINE_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px 0',
  flex: '1 1 auto',
};

class RouteErrorBoundaryInner extends Component<
  RouteErrorBoundaryInnerProps,
  RouteErrorBoundaryInnerState
> {
  state: RouteErrorBoundaryInnerState = { hasError: false, error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Pick<RouteErrorBoundaryInnerState, 'hasError' | 'error'> {
    return { hasError: true, error };
  }

  /**
   * Reset theo `resetKey` phải làm ở đây chứ KHÔNG phải `componentDidUpdate` +
   * `setState`: cách kia bắt React render thêm một lượt màn lỗi rồi mới render
   * trang mới, người dùng thấy màn lỗi nhấp nháy khi chuyển trang.
   */
  static getDerivedStateFromProps(
    props: RouteErrorBoundaryInnerProps,
    state: RouteErrorBoundaryInnerState,
  ): RouteErrorBoundaryInnerState | null {
    if (props.resetKey !== state.resetKey) {
      return { hasError: false, error: null, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const { resetKey, context, onError } = this.props;
    console.error('[RouteErrorBoundary]', {
      route: resetKey,
      tenant_id: context?.tenant_id,
      user_id: context?.user_id,
      error,
      componentStack: info.componentStack,
    });
    // Điểm cắm reporter (Sentry, ...) về sau mà không phải sửa chỗ gắn boundary.
    onError?.(error, info);
  }

  private handleRetry = (): void => {
    // Lỗi chunk-load: `React.lazy()` đã cache promise reject vĩnh viễn, soft
    // reset chỉ khiến React ném lại đúng lỗi cũ ngay lập tức — phải reload
    // cứng để lấy index.html/manifest asset mới.
    if (isLazyChunkError(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const fullScreen = Boolean(this.props.fullScreen);
      return (
        <div
          data-testid="route-error-fallback"
          data-fullscreen={fullScreen ? 'true' : 'false'}
          style={fullScreen ? FULL_SCREEN_STYLE : INLINE_STYLE}
        >
          {/* Cố ý KHÔNG hiện message/stack: người dùng cuối không dùng được,
              còn tên biến nội bộ thì không nên lộ. Chi tiết nằm ở console. */}
          <Result
            status="error"
            title={FALLBACK_TITLE}
            subTitle={FALLBACK_DESCRIPTION}
            extra={[
              <Button key="retry" type="primary" onClick={this.handleRetry}>
                Thử lại
              </Button>,
              <Button key="home" onClick={this.props.onGoHome}>
                Về trang chủ
              </Button>,
            ]}
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export interface RouteErrorBoundaryProps {
  children: ReactNode;
  /** true = màn lỗi chiếm trọn viewport (dùng cho tầng ngoài cùng, bọc `<Routes>`). */
  fullScreen?: boolean;
  onError?: (error: Error, info: ErrorInfo) => void;
}

/** Chỉ lấy đúng nhánh `auth` — boundary phải sống được cả khi store chưa hydrate. */
interface AuthStateSlice {
  auth?: { user?: AuthUser | null };
}

export function RouteErrorBoundary({ children, fullScreen, onError }: RouteErrorBoundaryProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  /**
   * Đọc thẳng redux thay vì `useAuth()`: `useAuth` truy cập `state.auth.user`
   * không optional, nên store chưa hydrate (hoặc test mock state rỗng) sẽ ném
   * lỗi NGAY TRONG boundary — thứ đáng lẽ phải bắt lỗi lại tự làm trắng trang.
   * `AuthUser` chỉ có `id` + `tenantId`, đó là hai field được log.
   */
  const user = useSelector((state: AuthStateSlice) => state.auth?.user ?? null);

  const goHome = (): void => {
    if (fullScreen) {
      // Tầng ngoài cùng: router có thể chính là thứ đang hỏng → reload hẳn.
      window.location.assign('/');
      return;
    }
    navigate('/');
  };

  return (
    <RouteErrorBoundaryInner
      resetKey={pathname}
      fullScreen={fullScreen}
      context={{ tenant_id: user?.tenantId, user_id: user?.id }}
      onError={onError}
      onGoHome={goHome}
    >
      {children}
    </RouteErrorBoundaryInner>
  );
}

export default RouteErrorBoundary;
